// Unit registry: the global library from `seed/units.json` plus optional org custom units.
// Factors live only in that JSON file (doc 05 §1.2) — never retype them here.
import seed from '../../../../seed/units.json';
import { UNIT_FUNCTIONS, type UnitFunctionName } from './functions';

export type ToBase =
  | { type: 'linear'; factor: number; offset: number }
  | { type: 'function'; fn: string; note?: string };

export interface Unit {
  id: string;
  symbol: string;
  name: string;
  quantity: string;
  toBase: ToBase;
  /** Set for org custom units; absent for the global library. */
  custom?: boolean;
}

export interface Quantity {
  key: string;
  name: string;
  baseUnitId: string;
  defaultDecimals: number;
  units: Unit[];
}

export interface UnitSystem {
  key: string;
  name: string;
  defaults: Record<string, string>;
}

/** An org-defined linear unit (doc 05 §1.5), already expressed relative to the quantity base. */
export interface CustomUnitInput {
  id: string;
  symbol: string;
  name: string;
  quantity: string;
  factor: number;
  offset: number;
}

interface SeedUnit {
  id: string;
  symbol: string;
  name: string;
  toBase: ToBase;
}
interface SeedQuantity {
  key: string;
  name: string;
  baseUnitId: string;
  defaultDecimals: number;
  units: SeedUnit[];
}
interface SeedFile {
  version: number;
  atmosphericPressurePa: number;
  quantities: SeedQuantity[];
  unitSystems: UnitSystem[];
}

const seedFile = seed as unknown as SeedFile;

export class UnitRegistry {
  readonly quantities: Quantity[];
  readonly unitSystems: UnitSystem[];
  private readonly unitsById = new Map<string, Unit>();
  private readonly quantitiesByKey = new Map<string, Quantity>();

  constructor(custom: readonly CustomUnitInput[] = [], customSystems: readonly UnitSystem[] = []) {
    this.quantities = seedFile.quantities.map((q) => ({
      key: q.key,
      name: q.name,
      baseUnitId: q.baseUnitId,
      defaultDecimals: q.defaultDecimals,
      units: q.units.map((u) => ({ ...u, quantity: q.key })),
    }));
    for (const q of this.quantities) {
      this.quantitiesByKey.set(q.key, q);
      for (const u of q.units) this.unitsById.set(u.id, u);
    }
    for (const c of custom) {
      const q = this.quantitiesByKey.get(c.quantity);
      // Custom units may extend a quantity but never shadow a global unit id.
      if (!q || this.unitsById.has(c.id)) continue;
      const unit: Unit = {
        id: c.id,
        symbol: c.symbol,
        name: c.name,
        quantity: c.quantity,
        toBase: { type: 'linear', factor: c.factor, offset: c.offset },
        custom: true,
      };
      q.units.push(unit);
      this.unitsById.set(unit.id, unit);
    }
    this.unitSystems = [...seedFile.unitSystems, ...customSystems];
  }

  unit(id: string): Unit | undefined {
    return this.unitsById.get(id);
  }

  requireUnit(id: string): Unit {
    const u = this.unitsById.get(id);
    if (!u) throw new Error(`Unknown unit "${id}"`);
    return u;
  }

  quantity(key: string): Quantity | undefined {
    return this.quantitiesByKey.get(key);
  }

  unitsFor(quantityKey: string): Unit[] {
    return this.quantitiesByKey.get(quantityKey)?.units ?? [];
  }

  unitSystem(key: string): UnitSystem | undefined {
    return this.unitSystems.find((s) => s.key === key);
  }

  private toBaseFn(unit: Unit): (v: number) => number {
    if (unit.toBase.type === 'linear') {
      const { factor, offset } = unit.toBase;
      return (v) => v * factor + offset;
    }
    const fn = UNIT_FUNCTIONS[unit.toBase.fn as UnitFunctionName];
    if (!fn) throw new Error(`Unit "${unit.id}" uses unknown function "${unit.toBase.fn}"`);
    return fn.toBase;
  }

  private fromBaseFn(unit: Unit): (v: number) => number {
    if (unit.toBase.type === 'linear') {
      const { factor, offset } = unit.toBase;
      return (v) => (v - offset) / factor;
    }
    const fn = UNIT_FUNCTIONS[unit.toBase.fn as UnitFunctionName];
    if (!fn) throw new Error(`Unit "${unit.id}" uses unknown function "${unit.toBase.fn}"`);
    return fn.fromBase;
  }

  /** Precomputed converter for hot paths. Throws if the units belong to different quantities. */
  converter(fromUnitId: string, toUnitId: string): (v: number) => number {
    if (fromUnitId === toUnitId) return identity;
    const from = this.requireUnit(fromUnitId);
    const to = this.requireUnit(toUnitId);
    if (from.quantity !== to.quantity) {
      throw new Error(
        `Cannot convert ${fromUnitId} (${from.quantity}) to ${toUnitId} (${to.quantity})`,
      );
    }
    if (from.toBase.type === 'linear' && to.toBase.type === 'linear') {
      // Collapse to a single multiply-add so long series convert in one pass.
      const scale = from.toBase.factor / to.toBase.factor;
      const shift = (from.toBase.offset - to.toBase.offset) / to.toBase.factor;
      return (v) => v * scale + shift;
    }
    const toBase = this.toBaseFn(from);
    const fromBase = this.fromBaseFn(to);
    return (v) => fromBase(toBase(v));
  }

  convert(value: number, fromUnitId: string, toUnitId: string): number {
    return this.converter(fromUnitId, toUnitId)(value);
  }

  /** `"2,145.0 psi"` — locale-formatted number followed by the unit symbol. */
  format(
    value: number | null | undefined,
    unitId: string | null | undefined,
    opts: { decimals?: number; locale?: string; withUnit?: boolean } = {},
  ): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    const unit = unitId ? this.unit(unitId) : undefined;
    const decimals =
      opts.decimals ?? (unit ? (this.quantity(unit.quantity)?.defaultDecimals ?? 2) : 2);
    const text = new Intl.NumberFormat(opts.locale ?? 'en', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
    if (opts.withUnit === false || !unit || unit.symbol === '') return text;
    return `${text} ${unit.symbol}`;
  }
}

function identity(v: number): number {
  return v;
}

export const ATMOSPHERIC_PRESSURE_PA = seedFile.atmosphericPressurePa;
export const UNITS_LIBRARY_VERSION = seedFile.version;

/** Registry of the global library only. API and web build org-specific ones when needed. */
export const globalUnits = new UnitRegistry();
